import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { authedAtTimestampAtom } from '../auth/state';
import { useGetTelehealthSessionInfo } from '../hooks/useGetTelehealthSessionInfo';
import { useDesiredVideoPlatform, useVideoPlatform } from '../hooks/useVideoPlatform';
import { recordJourneyStep, setJourneyContext, setJourneyUserType } from '../journey/journeySteps';
import { useJourneyPageExit } from '../journey/useJourneyPageExit';
import { VisitorState, visitorStateAtom } from './state';

const ENTRY_STATES = new Set<VisitorState>([VisitorState.INTRO, VisitorState.PRESESSION_MEASURES]);

/**
 * Client steps visible from the page and the visitor state machine. Check-in
 * and deliver are recorded at their server round trips; the rest come from
 * tracking events.
 */
export function useVisitorJourneySteps({
  isLoading,
  isValidUrl,
  isBlocked,
}: {
  isLoading: boolean;
  isValidUrl: boolean;
  isBlocked: boolean;
}) {
  const { providerShortId, patientShortId } = useParams<{ providerShortId: string; patientShortId: string }>();
  const { data, loading: sessionInfoLoading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const visitorState = useAtomValue(visitorStateAtom);
  const authedAtTimestamp = useAtomValue(authedAtTimestampAtom);
  const previousStateRef = useRef<VisitorState>();
  // desired platform until the adapter exists
  const videoPlatform = useVideoPlatform();
  const desiredVideoPlatform = useDesiredVideoPlatform();

  useEffect(function setUserType() {
    setJourneyUserType('client');
  }, []);

  // before recordReachAndLoad so the first events carry the context
  useEffect(
    function setEventContext() {
      setJourneyContext({
        video_platform: videoPlatform ?? desiredVideoPlatform,
        appointment_short_id: appointmentShortId,
      });
    },
    [videoPlatform, desiredVideoPlatform, appointmentShortId],
  );

  useJourneyPageExit();

  useEffect(
    function recordReachAndLoad() {
      if (isLoading || sessionInfoLoading) return;
      if (isBlocked) {
        recordJourneyStep('arriving.reach', { status: 'error', reason: 'blocked' });
        return;
      }
      if (!isValidUrl) {
        recordJourneyStep('arriving.reach', { status: 'error', reason: 'invalid_link' });
        return;
      }
      recordJourneyStep('arriving.reach', { appointment_resolved: !!appointmentShortId });
      recordJourneyStep('arriving.load');
    },
    [isLoading, sessionInfoLoading, isValidUrl, isBlocked, appointmentShortId],
  );

  useEffect(
    function recordStateTransitions() {
      const previous = previousStateRef.current;
      previousStateRef.current = visitorState;
      const authenticated = !!authedAtTimestamp;

      // returning client: identity was settled on an earlier visit
      if (previous === undefined && !ENTRY_STATES.has(visitorState)) {
        recordJourneyStep('arriving.identify', { authenticated });
        recordJourneyStep('arriving.prepare', { status: 'skipped' });
      }
      if (previous === undefined) return;

      if (ENTRY_STATES.has(previous) && !ENTRY_STATES.has(visitorState)) {
        recordJourneyStep('arriving.identify', { authenticated });
        recordJourneyStep('arriving.prepare', { measures: previous === VisitorState.PRESESSION_MEASURES });
      } else if (previous === VisitorState.INTRO && visitorState === VisitorState.PRESESSION_MEASURES) {
        recordJourneyStep('arriving.identify', { authenticated });
      }

      if (previous === VisitorState.WAITING) {
        if (visitorState === VisitorState.JOINING) {
          recordJourneyStep('waiting.held');
        } else if (visitorState === VisitorState.TIMEOUT) {
          recordJourneyStep('waiting.held', { status: 'error', reason: 'timeout' });
        } else if (visitorState === VisitorState.TAB_AWAY) {
          recordJourneyStep('waiting.held', { status: 'error', reason: 'tab_away' });
        }
      }

      if (previous === VisitorState.JOINING) {
        if (visitorState === VisitorState.MEETING) {
          recordJourneyStep('joining.connect');
        } else if (visitorState === VisitorState.WAITING) {
          recordJourneyStep('joining.connect', { status: 'error', reason: 'join_failed' });
        }
      }
    },
    [visitorState, authedAtTimestamp],
  );
}
